import React, { useEffect, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../../../../../../utils/CurrencyUtils';
import { CaretLeft, CaretRight } from '@phosphor-icons/react';
import { TruckRevenueExpenseData } from '../../../../../../../types/apiTypes';
import { ClipLoader } from 'react-spinners';
import { buildPageWindow } from '../../../../../../../utils/PaginationUtils';
import { DemoDataWrapper } from '../../../home/components/DemoDataBadge';

const ITEMS_PER_PAGE = 2;
const SEGMENT_SEPARATOR = '#ffffff';
const SEGMENT_SEPARATOR_WIDTH = 2;

const TruckRevenueExpenseBar: React.FC<{
  truck: TruckRevenueExpenseData;
  isAllTrucks?: boolean;
}> = ({ truck, isAllTrucks = false }) => {
  const [hoveredSegment, setHoveredSegment] = useState<string | null>(null);

  // Prepare data for revenue bar
  const revenueData = [
    {
      name: 'Revenue',
      revenue: truck.revenue,
      netTakeHome: truck.netTakeHome,
    },
  ];

  // Prepare data for expenses bar
  const totalExpenses = truck.expenses.reduce(
    (sum, exp) => sum + exp.amount,
    0
  );
  const expenseData = [
    {
      name: 'Expenses',
      ...truck.expenses.reduce(
        (acc, exp) => {
          acc[exp.category] = exp.amount;
          return acc;
        },
        {} as Record<string, number>
      ),
    },
  ];

  // Calculate which bar should be full width
  const totalRevenue = truck.revenue + truck.netTakeHome;
  const maxAmount = Math.max(totalRevenue, totalExpenses);

  const renderName = (name: string) => {
    if (name === 'Revenue') return 'Remaining Revenue';
    return name;
  };

  // Custom tooltip that shows based on hoveredSegment state
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length > 0 && hoveredSegment) {
      // Find the entry that matches our hovered segment
      const activeEntry = payload.find(
        (entry: any) =>
          entry.dataKey === hoveredSegment || entry.name === hoveredSegment
      );

      if (!activeEntry || !activeEntry.value) return null;

      // Get the color for this segment
      let color = activeEntry.fill;
      if (hoveredSegment === 'revenue') color = '#15803d';
      else if (hoveredSegment === 'netTakeHome') color = '#86efac';
      else {
        const expenseItem = truck.expenses.find(
          (e) => e.category === hoveredSegment
        );
        if (expenseItem) color = expenseItem.color;
      }

      return (
        <div className="rounded-lg bg-gray-800 p-3 text-white shadow-lg">
          <div className="flex items-center">
            <span
              className="mr-2 inline-block h-3 w-3 rounded"
              style={{ backgroundColor: color }}
            ></span>
            <p className="text-[14px] font-semibold">
              {renderName(activeEntry.name || hoveredSegment)}:{' '}
              {formatCurrency(activeEntry.value)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={``}>
      <h3 className={`text-lg font-semibold text-gray-900`}>
        {truck.truckName}
      </h3>

      {/* Revenue Bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Revenue</span>
          <span className="text-sm font-semibold text-green-700">
            Total: {formatCurrency(truck.revenue + truck.netTakeHome)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={20}>
          <BarChart
            layout="vertical"
            data={revenueData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            className="rounded-lg"
          >
            <XAxis type="number" hide domain={[0, maxAmount]} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
              position={{ x: undefined, y: undefined }}
            />
            <Bar
              dataKey="revenue"
              stackId="a"
              fill="#15803d"
              stroke={SEGMENT_SEPARATOR}
              strokeWidth={SEGMENT_SEPARATOR_WIDTH}
              name="Revenue"
              radius={[4, 0, 0, 4]}
              onMouseEnter={() => setHoveredSegment('revenue')}
              onMouseLeave={() => setHoveredSegment(null)}
            />
            <Bar
              dataKey="netTakeHome"
              stackId="a"
              fill="#86efac"
              stroke={SEGMENT_SEPARATOR}
              strokeWidth={SEGMENT_SEPARATOR_WIDTH}
              name="Net Take Home"
              radius={[0, 4, 4, 0]}
              onMouseEnter={() => setHoveredSegment('netTakeHome')}
              onMouseLeave={() => setHoveredSegment(null)}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Expenses Bar */}
      <div>
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Expenses</span>
          <span className="text-sm font-semibold text-red-700">
            Total: {formatCurrency(totalExpenses)}
          </span>
        </div>
        <ResponsiveContainer width="100%" height={20}>
          <BarChart
            layout="vertical"
            data={expenseData}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          >
            <XAxis type="number" hide domain={[0, maxAmount]} />
            <YAxis type="category" dataKey="name" hide />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
              position={{ x: undefined, y: undefined }}
            />
            {truck.expenses.map((exp, index) => (
              <Bar
                key={exp.category}
                dataKey={exp.category}
                stackId="a"
                stroke={SEGMENT_SEPARATOR}
                strokeWidth={SEGMENT_SEPARATOR_WIDTH}
                fill={exp.color}
                name={exp.category}
                radius={
                  index === 0
                    ? [4, 0, 0, 4] // First bar - rounded left
                    : index === truck.expenses.length - 1
                      ? [0, 4, 4, 0] // Last bar - rounded right
                      : undefined // Middle bars - no radius
                }
                onMouseEnter={() => setHoveredSegment(exp.category)}
                onMouseLeave={() => setHoveredSegment(null)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      {/* <div className="mt-4 flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-700 rounded"></div>
          <span>Revenue</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-green-300 rounded"></div>
          <span>Net Take Home</span>
        </div>
        {truck.expenses.map((exp) => (
          <div key={exp.category} className="flex items-center gap-2">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: exp.color }}></div>
            <span>{exp.category}</span>
          </div>
        ))}
      </div> */}
    </div>
  );
};

interface RevenuevsExpensesStackedBarsProps {
  addClass?: string;
  trucksData: TruckRevenueExpenseData[];
  loading: boolean;
  selectedUnitNumber?: string;
  isPreActive: boolean;
}

const RevenuevsExpensesStackedBars: React.FC<
  RevenuevsExpensesStackedBarsProps
> = ({
  addClass,
  trucksData = [],
  loading,
  selectedUnitNumber,
  isPreActive,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  // Separate "All Trucks" from individual trucks
  const allTrucksData = trucksData.find((t) => t.truckId === 'all');

  const individualTrucks = trucksData.filter((t) => t.truckId !== 'all');

  // Calculate pagination
  const totalPagesRaw = Math.ceil(individualTrucks.length / ITEMS_PER_PAGE);
  const totalPages = Math.max(1, totalPagesRaw);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentTrucks = individualTrucks.slice(startIndex, endIndex);

  const hasPagination = totalPages > 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1); // or Math.min(currentPage, totalPages)
    }
  }, [currentPage, totalPages]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <DemoDataWrapper showBadge={isPreActive}>
      <div
        className={`flex h-[600px] max-h-[600px] w-full flex-col overflow-hidden border-[1px] border-bordergray bg-white ${addClass}`}
      >
        <div className="flex h-[71px] min-h-[71px] items-center justify-start border-b-[1px] border-bordergray p-4">
          <h2 className="flex items-center text-[16px] font-semibold leading-[24px] text-primary">
            Revenue & Expenses Breakdown
            {selectedUnitNumber && (
              <span className="ml-2 block text-sm font-normal text-graysecondary">
                {selectedUnitNumber}
              </span>
            )}
          </h2>
        </div>

        {loading ? (
          <div className="flex h-[600px] items-center justify-center p-8">
            <ClipLoader
              color="#1D213B"
              loading={loading}
              size={60}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </div>
        ) : trucksData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <p className="mb-2 text-[16px] font-semibold leading-[24px] text-primary">
              No Data Available
            </p>
            <p className="text-[14px] leading-[20px] text-graysecondary">
              Revenue and expense data will appear here once transactions are
              recorded for the selected date range.
            </p>
          </div>
        ) : (
          <div
            className={`flex h-full flex-col ${hasPagination ? 'justify-between' : 'justify-start'} px-4 pt-4`}
          >
            {/* All Trucks Summary */}
            <div>
              {allTrucksData && (
                <div className="mb-2 rounded-lg border-[1px] border-bordergray bg-gray-50 p-2">
                  <TruckRevenueExpenseBar
                    truck={allTrucksData}
                    isAllTrucks={true}
                  />
                </div>
              )}
              <div className="space-y-2">
                {currentTrucks.map((truck, idx) => (
                  <div
                    className={`${idx !== currentTrucks.length - 1 && `border-b-[1px] border-bordergray`} p-2`}
                  >
                    <TruckRevenueExpenseBar key={truck.truckId} truck={truck} />
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination Controls */}
            {hasPagination && (
              <div className="mt-6 flex items-center justify-between pb-4">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Previous page"
                >
                  <CaretLeft className="h-4 w-4" />
                  Previous
                </button>

                <div className="mx-2 flex items-center justify-center gap-2 whitespace-nowrap">
                  {buildPageWindow(
                    totalPages,
                    currentPage,
                    7, // <= force compact; last appears only near the end
                    { revealLastOffset: 4 } // last shows starting at page 6 of 10
                  ).map((item, idx) =>
                    item === '…' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="shrink-0 px-1 text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => setCurrentPage(item)}
                        className={`h-8 w-8 shrink-0 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === item
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                        aria-current={currentPage === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                  aria-label="Next page"
                >
                  Next
                  <CaretRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DemoDataWrapper>
  );
};

export default RevenuevsExpensesStackedBars;
